import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';

import { cn } from '@/shadcn-ui/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

type PopoverConfiguration = {
  displayValue: string;
  align?: 'start' | 'center' | 'end';
  contentClassname?: string;
  portalRef?: HTMLElement;
  // Flag indicating whether to display the displayValue inside the SliderThumb component from Radix or to use a Popover.
  insideThumb?: boolean;
};

type EdgeLabelsConfiguration = {
  minDisplayValue: string;
  maxDisplayValue: string;
};

interface SliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  rootClassName?: string;
  popoverConfiguration?: PopoverConfiguration[];
  showStepIndicators?: boolean;
  edgeLabelsConfiguration?: EdgeLabelsConfiguration;
}

function Indicators({
  min,
  max,
  selectedValue,
}: {
  min: number;
  max: number;
  /** Current selected value of slider */
  selectedValue: number;
}) {
  return (
    <div className="relative">
      <div className="pointer-events-none absolute top-[-9px] flex w-full flex-row justify-between pl-[4px]">
        {
          /** Adjust tick count by range of slider
           * ex:
           *  max = 12, min = 6, 1, length = 7
           */
          Array.from({ length: max - min + 1 }).map((_, i) => {
            /** To allow for a track that does not start at 0, add min to index to correct index to slider range
             * ex:
             *  min = 6, i = 0, tickStep = 6
             *  min = 6, i = 3, tickStep = 9
             */
            const tickStep = i + min;
            return (
              <p
                className={cn('body select-none', {
                  'text-flexcar-neutral100': tickStep < selectedValue,
                  'text-flexcar-blue30': tickStep > selectedValue,
                })}
                key={`tick-${i}`}
              >
                {tickStep !== min && tickStep !== max && max ? `\u00B7` : null}
              </p>
            );
          })
        }
      </div>
    </div>
  );
}

interface SliderThumbContainerProps {
  children: React.ReactNode;
  showPopover: boolean;
  popoverConfiguration?: PopoverConfiguration;
}

function SliderThumbContainer({
  children,
  showPopover,
  popoverConfiguration,
}: SliderThumbContainerProps) {
  return popoverConfiguration && !popoverConfiguration.insideThumb ? (
    <Popover open={showPopover}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={popoverConfiguration?.align ?? 'center'}
        className={cn('bg-none p-0 shadow-none', popoverConfiguration?.contentClassname)}
        sideOffset={2}
        portalContainer={popoverConfiguration?.portalRef}
      >
        <p className="body2 text-flexcar-neutral1">{popoverConfiguration.displayValue}</p>
      </PopoverContent>
    </Popover>
  ) : (
    children
  );
}

interface SliderThumbProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Thumb> {
  // When this is specified, the thumb will display the value from the popover configuration's thumbDisplayValue property
  thumbDisplayValue?: string;
}

const SliderThumb = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Thumb>,
  SliderThumbProps
>(({ thumbDisplayValue, ...props }: SliderThumbProps, ref) => {
  return thumbDisplayValue ? (
    <SliderPrimitive.Thumb
      ref={ref}
      {...props}
      className="relative block h-[20px] w-[20px] cursor-pointer rounded-full border border-flexcar-neutral60 bg-flexcar-neutral100 ring-offset-background transition-colors focus-visible:shadow-sliderShadow focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
    >
      <div
        className={cn(
          'absolute left-1/2 top-1/2 mt-[24px] -translate-x-1/2 -translate-y-1/2 transform'
        )}
      >
        <p className="body2 text-flexcar-neutral1">{thumbDisplayValue}</p>
      </div>
    </SliderPrimitive.Thumb>
  ) : (
    <SliderPrimitive.Thumb
      ref={ref}
      {...props}
      className="block h-[20px] w-[20px] cursor-pointer rounded-full border border-flexcar-neutral60 bg-flexcar-neutral100 ring-offset-background transition-colors focus-visible:shadow-sliderShadow focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
    />
  );
});
SliderThumb.displayName = SliderPrimitive.Thumb.displayName;

function EdgeLabels({ configuration }: { configuration: EdgeLabelsConfiguration }) {
  return (
    <div className="absolute top-[16px] flex w-full items-center justify-between">
      <p className="body2 w-fit text-flexcar-neutral50">
        {configuration.minDisplayValue}
      </p>
      <p className="body2 w-fit text-flexcar-neutral50">
        {configuration.maxDisplayValue}
      </p>
    </div>
  );
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      rootClassName,
      popoverConfiguration,
      showStepIndicators,
      edgeLabelsConfiguration,
      ...props
    }: SliderProps,
    ref
  ) => {
    const showPopover = popoverConfiguration !== undefined;
    const showEdgeLabels = edgeLabelsConfiguration !== undefined;

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          'relative flex w-full touch-none select-none items-center',
          rootClassName
        )}
        {...props}
      >
        <SliderPrimitive.Track
          className={cn(
            'relative h-[4px] w-full grow overflow-hidden rounded-full bg-flexcar-neutral90'
          )}
        >
          <SliderPrimitive.Range className="absolute h-full bg-flexcar-neutral1" />
          {showStepIndicators && props.min && props.max && props.value ? (
            <Indicators min={props.min} max={props.max} selectedValue={props.value[0]} />
          ) : null}
        </SliderPrimitive.Track>
        {props.value && Array.isArray(props.value) ? (
          <>
            {props.value.map((_, index) => (
              <SliderThumbContainer
                showPopover={showPopover}
                popoverConfiguration={popoverConfiguration?.at(index)}
                key={index}
              >
                <SliderThumb
                  thumbDisplayValue={
                    popoverConfiguration?.at(index)?.insideThumb
                      ? popoverConfiguration?.at(index)?.displayValue
                      : undefined
                  }
                />
              </SliderThumbContainer>
            ))}
          </>
        ) : (
          <SliderThumbContainer
            showPopover={showPopover}
            popoverConfiguration={popoverConfiguration?.at(0)}
          >
            <SliderThumb />
          </SliderThumbContainer>
        )}
        {showEdgeLabels ? <EdgeLabels configuration={edgeLabelsConfiguration} /> : null}
      </SliderPrimitive.Root>
    );
  }
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
